/**
 * Wraps HTML content with GrapesJS editor interface
 */
export const wrapWithGrapesJS = (htmlContent: string, cssContent: string = '', jsContent: string = ''): string => {
    // Extract the complete body element with its attributes
    let bodyContent = '';
    const bodyMatch = htmlContent.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
    
    if (bodyMatch) {
        bodyContent = bodyMatch[1]; // Content inside body tags
    } else {
        // If no body tags, check if it's just HTML content without full document structure
        const hasHtmlTags = htmlContent.includes('<html') || htmlContent.includes('<head') || htmlContent.includes('<body');
        if (!hasHtmlTags) {
            bodyContent = htmlContent;
        }
    }

    // Escape CSS and JS content for safe injection
    const escapedCssContent = cssContent.replace(/`/g, '\\`').replace(/\$/g, '\\$');
    const escapedJsContent = jsContent.replace(/`/g, '\\`').replace(/\$/g, '\\$');

    // Generate GrapesJS dependencies
    const generateDependencies = (): string => {
        const dependencies = [
            '<link rel="stylesheet" href="//unpkg.com/grapesjs/dist/css/grapes.min.css" />',
            '<script src="//unpkg.com/grapesjs"></script>',
            '<script src="//unpkg.com/grapesjs-tailwind"></script>'
        ];
        
        if (cssContent && cssContent.trim()) {
            dependencies.push(`<style id="project-styles">
/* === PROJECT CSS FILES === */
${cssContent}
/* === END PROJECT CSS === */
</style>`);
        }
        
        return dependencies.join('\n');
    };

    // Generate the complete GrapesJS editor interface
    const generateEditorInterface = (): string => {
        return `
<div class="editor-container">
  <!-- Left Panel: Element Library -->
  <div class="editor-left-panel">
    <div class="panel-header">
      <h3>Add Elements</h3>
      <p class="drag-drop-hint">Drag and drop to add elements</p>
    </div>
    
    <!-- Element Categories Tabs -->
    <!-- <div class="element-tabs">
      <button class="tab-btn active" data-category="basic">Basic</button>
      <button class="tab-btn" data-category="layout">Layout</button>
      <button class="tab-btn" data-category="media">Media</button>
      <button class="tab-btn" data-category="advanced">Advanced</button>
    </div> -->
    
    <!-- Element Blocks Container -->
    <div class="elements-container">
      <!-- <div class="element-category active" id="category-basic">
        <div id="blocks-basic"></div>
      </div>
      <div class="element-category" id="category-layout">
        <div id="blocks-layout"></div>
      </div> -->
      <div class="element-category active" id="category-media">
        <div id="blocks-media"></div>
      </div>
      <!-- <div class="element-category" id="category-advanced">
        <div id="blocks-advanced"></div>
      </div> -->
    </div>
  </div>

  <!-- Main Editor Area -->
  <div class="editor-main">
    <!-- Enhanced Toolbar -->
    <div class="editor-toolbar">
      <div class="toolbar-left">
        <div class="action-group">
          <button id="btn-undo" title="Undo (Ctrl+Z)" class="toolbar-btn">
            Undo
        </button>
          <button id="btn-redo" title="Redo (Ctrl+Y)" class="toolbar-btn">
            Redo
        </button>
      </div>
      </div>
      
      <div class="toolbar-center">
        <div class="device-selector-group">
          <label>Device:</label>
          <select id="device-selector" class="device-select">
          <option value="Desktop">🖥️ Desktop</option>
          <option value="Tablet">📱 Tablet</option>
          <option value="Mobile">📲 Mobile</option>
        </select>
      </div>
      </div>
      
      <div class="toolbar-right">
        <button id="btn-save" class="save-btn" style="display: none;" title="Save Changes (Ctrl+S)">
          SAVE CHANGES
        </button>
      </div>
    </div>
    
    <!-- Canvas -->
    <div class="editor-canvas">
      <div id="gjs">${bodyContent}</div>
    </div>
  </div>

  <!-- Right Panel: Design & Properties -->
  <!-- <div class="editor-right-panel">
    <!-- Panel Tabs -->
    <!-- <div class="right-panel-tabs">
      <button class="right-tab-btn active" data-panel="design">
        Design
      </button>
      <button class="right-tab-btn" data-panel="settings">
        Settings
      </button>
    </div> -->
    
    <!-- Panel Content -->
    <!-- <div class="right-panel-content">
      <!-- Design Panel -->
      <!-- <div class="right-panel-section active" id="panel-design">
        <div class="style-section">
          <h4>Appearance</h4>
          <div id="styles-container"></div>
        </div>
      </div> -->
      
      <!-- Settings Panel -->
      <!-- <div class="right-panel-section" id="panel-settings">
        <div class="traits-section">
          <h4>Element Properties</h4>
          <div id="traits-container"></div>
        </div>
      </div> -->
    <!-- </div> -->
  <!-- </div> -->
</div>`;
    };

    // Check if the input has HTML structure
    const hasHtmlStructure = /<html[^>]*>/i.test(htmlContent) && /<\/html>/i.test(htmlContent);
    const hasHeadTag = /<head[^>]*>/i.test(htmlContent) && /<\/head>/i.test(htmlContent);
    const hasBodyTag = /<body[^>]*>/i.test(htmlContent) && /<\/body>/i.test(htmlContent);

    // Generate the final HTML based on the input structure
    let finalHtml: string;
    
    if (hasHtmlStructure) {
        // Input has HTML structure - preserve original structure and inject editor
        let result = htmlContent;
        
        // Add dependencies to head if it exists, or create head if it doesn't
        if (hasHeadTag) {
            const headMatch = result.match(/<head[^>]*>([\s\S]*?)<\/head>/i);
            if (headMatch) {
                const existingHeadContent = headMatch[1];
                const newHeadContent = existingHeadContent + '\n' + generateDependencies();
                result = result.replace(headMatch[0], `<head>${newHeadContent}</head>`);
            }
        } else {
            // Create head tag with dependencies
            const headContent = `
<meta charset="utf-8">
<title>Tericsoft Page Editor</title>
${generateDependencies()}`;
            result = result.replace('<html>', `<html>\n<head>${headContent}\n</head>`);
        }
        
        // Replace body content with editor interface while preserving body attributes
        if (hasBodyTag) {
            const bodyMatch = result.match(/<body[^>]*>[\s\S]*?<\/body>/i);
            if (bodyMatch) {
                const bodyAttributesMatch = bodyMatch[0].match(/<body([^>]*)>/i);
                const bodyAttributes = bodyAttributesMatch ? bodyAttributesMatch[1] : '';
                const editorInterface = generateEditorInterface();
                result = result.replace(bodyMatch[0], `<body${bodyAttributes}>${editorInterface}</body>`);
            }
        } else {
            // Add body with editor interface
            const editorInterface = generateEditorInterface();
            result = result.replace('</html>', `<body>${editorInterface}</body>\n</html>`);
        }
        
        finalHtml = result;
    } else {
        // Input has no HTML structure - create complete document
        finalHtml = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>Tericsoft Page Editor</title>
${generateDependencies()}
</head>
<body>${generateEditorInterface()}</body>
</html>`;
    }

    // Add the CSS styles and JavaScript
    return `${finalHtml}
<style>
/* Custom GrapesJS Dark Theme - Full Canvas Version */
/* Reset only for editor elements to avoid conflicts with original page */
html{
border-radius: 0px !important;
}
.editor-container * {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.gjs-frame-wrapper{
left:50% !important;
right:50% !important;
transform: translateX(-50%) !important;
}

/* Preserve original body styling but ensure editor container is positioned correctly */
.editor-container {
  display: flex;
  height: 100vh;
  width: 100vw;
  font-family: 'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  background: #0f1419;
  color: #e5e7eb;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 9999;
  margin: 0;
  padding: 0;
}

/* Ensure original page content is hidden when editor is active */
body:has(.editor-container) {
  overflow: hidden;
}

/* Preserve original page styling in the canvas */
#gjs {
  background: transparent !important;
}

#gjs-canvas {
  background: transparent !important;
}

.gjs-cv-canvas {
  background: transparent !important;
}

.gjs-one-bg{
    background: #222222 !important;
}

/* Ensure the canvas preserves original page styling */
.gjs-frame {
  background: transparent !important;
}

.editor-left-panel {

  width: 250px;
  background: #1a1a1a;
  border-right: 1px solid #1a1a1a;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
}

.panel-header {
  padding: 16px;
  background: linear-gradient(135deg, #1f2937 0%, #374151 100%);
  border-bottom: 1px solid #4b5563;
}

.panel-header h3 {
  margin: 0 0 6px 0;
  color: #f9fafb;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.drag-drop-hint {
  margin: 0;
  color: #9ca3af;
  font-size: 11px;
  font-style: italic;
  opacity: 0.8;
}

.element-tabs {
  display: flex;
  background: #1a1a1a;
  border-bottom: 1px solid #374151;
}

.tab-btn {
  flex: 1;
  padding: 12px 8px;
  background: transparent;
  border: none;
  color: #9ca3af;
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  cursor: pointer;
  transition: all 0.3s ease;
  border-bottom: 2px solid transparent;
}

.tab-btn:hover {
  background: #374151;
  color: #e5e7eb;
}

.tab-btn.active {
  background: #374151;
  color: #60a5fa;
  border-bottom-color: #60a5fa;
}

.elements-container {
  flex: 1;
  overflow-y: auto;
}

.element-category {
  display: none;
  padding: 16px 12px;
}

.element-category.active {
  display: block;
}

.editor-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #111827;
  width: 100vw;
  min-width: 0;
}

.editor-toolbar {
  height: 36px;
  background: linear-gradient(135deg, #1f2937 0%, #374151 100%);
  color: #f9fafb;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 12px;
  border-bottom: 1px solid #4b5563;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  margin: 0;
  flex-shrink: 0;
}

.toolbar-left, .toolbar-center, .toolbar-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.action-group {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 2px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 4px;
}

.toolbar-btn {
  padding: 4px 10px;
  background: linear-gradient(135deg, #4b5563 0%, #6b7280 100%);
  border: 1px solid #6b7280;
  border-radius: 3px;
  color: #f9fafb;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 11px;
  font-weight: 500;
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;
  min-height: 24px;
}



.device-selector-group {
  display: flex;
  align-items: center;
  gap: 6px;
  background: rgba(0, 0, 0, 0.2);
  padding: 2px 8px;
  border-radius: 4px;
}

.device-selector-group label {
  color: #d1d5db;
  font-size: 10px;
  font-weight: 500;
  white-space: nowrap;
}

.device-select {
  background: linear-gradient(135deg, #6b7280 0%, #9ca3af 100%);
  color: #f9fafb;
  border: 1px solid #9ca3af;
  border-radius: 3px;
  padding: 3px 8px;
  font-size: 10px;
  font-weight: 500;
  cursor: pointer;
  min-width: 100px;
  height: 24px;
}

.editor-canvas {
  flex: 1;
  position: relative;
  background: #0f1419;
  width: 100%;
  height: calc(100vh - 36px);
  min-height: 0;
  overflow: hidden;
  margin: 0;
  padding: 0;
}

.editor-right-panel {

  width: 250px;
  background: #1a1a1a;
  border-left: 1px solid #374151;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
}

.right-panel-tabs {
  display: flex;
  background: #1a1a1a;
  border-bottom: 1px solid #374151;

}

.right-tab-btn {
  flex: 1;
  padding: 14px 12px;
  background: transparent;
  border: none;
  color: #9ca3af;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  cursor: pointer;
  transition: all 0.3s ease;
  border-bottom: 2px solid transparent;
  display: flex;
  align-items: center;
  justify-content: center;
}

.right-tab-btn:hover {
  background: #374151;
  color: #e5e7eb;
}

.right-tab-btn.active {
  background: #374151;
  color: #10b981;
  border-bottom-color: #10b981;
}

.right-panel-content {
  flex: 1;
  overflow-y: auto;
}

.right-panel-section {
  display: none;
  height: 100%;
}

.right-panel-section.active {
  display: block;
}

.style-section, .traits-section {
  padding: 0;
  height: 100%;
  overflow-y: auto;
}

.style-section h4, .traits-section h4 {
  margin: 0;
  padding: 16px;
  background: linear-gradient(135deg, #1f2937 0%, #374151 100%);
  font-size: 12px;
  font-weight: 600;
  border-bottom: 1px solid #4b5563;

  color: #f3f4f6;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

#styles-container, #traits-container {
  padding: 16px !important;
  background: #1a1a1a !important;
  min-height: 400px;
}

/* GrapesJS Style Manager specific styling */
.gjs-sm-sector {
  margin-bottom: 20px !important;
  background: #1f2937 !important;
  border-radius: 6px !important;
  overflow: hidden !important;
}

.gjs-sm-title {
  background: #374151 !important;
  color: #f9fafb !important;
  padding: 12px 16px !important;
  font-size: 11px !important;
  font-weight: 600 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  border: none !important;
  cursor: pointer !important;
}

.gjs-sm-properties {
  padding: 16px !important;
  background: #1f2937 !important;
}

.gjs-sm-property {
  margin-bottom: 12px !important;
  padding: 8px !important;
  background: #374151 !important;
  border-radius: 4px !important;
  border: 1px solid #4b5563 !important;
}

.gjs-sm-label {
  color: #e5e7eb !important;
  font-size: 11px !important;
  font-weight: 500 !important;
  margin-bottom: 4px !important;
  display: block !important;
}

.gjs-sm-field input, .gjs-sm-field select {
  width: 100% !important;
  padding: 6px 8px !important;
  background: #1a1a1a !important;
  border: 1px solid #6b7280 !important;
  border-radius: 4px !important;
  color: #f9fafb !important;
  font-size: 11px !important;
}

.gjs-sm-field input:focus, .gjs-sm-field select:focus {
  outline: none !important;
  border-color: #60a5fa !important;
  box-shadow: 0 0 0 2px rgba(96, 165, 250, 0.2) !important;
}

/* Traits Manager specific styling */
.gjs-trt-trait {
  margin-bottom: 12px !important;
  padding: 8px !important;
  background: #374151 !important;
  border-radius: 4px !important;
  border: 1px solid #4b5563 !important;
}

.gjs-trt-label {
  color: #e5e7eb !important;
  font-size: 11px !important;
  font-weight: 500 !important;
  margin-bottom: 4px !important;
  display: block !important;
}

.gjs-trt-field input, .gjs-trt-field select, .gjs-trt-field textarea {
  width: 100% !important;
  padding: 6px 8px !important;
  background: #1a1a1a !important;
  border: 1px solid #6b7280 !important;
  border-radius: 4px !important;
  color: #f9fafb !important;
  font-size: 11px !important;
  resize: vertical !important;
}

.gjs-trt-field input:focus, .gjs-trt-field select:focus, .gjs-trt-field textarea:focus {
  outline: none !important;
  border-color: #60a5fa !important;
  box-shadow: 0 0 0 2px rgba(96, 165, 250, 0.2) !important;
}

/* Color picker styling */
.gjs-field-color-picker {
  background: #1a1a1a !important;
  border: 1px solid #6b7280 !important;
  border-radius: 4px !important;
}

/* Checkbox and radio styling */
.gjs-field input[type="checkbox"], .gjs-field input[type="radio"] {
  width: auto !important;
  margin-right: 6px !important;

}



.gjs-block, .draggable-block {
  margin: 4px 0;
  padding: 12px 16px;
  background: linear-gradient(135deg, #374151 0%, #4b5563 100%);
  border: 2px dashed #6b7280;
  cursor: grab;

  text-align: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  color: #e5e7eb;
  font-size: 12px;
  font-weight: 500;
  position: relative;
  overflow: hidden;
  min-height: 40px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  user-select: none;
}

.gjs-block:active, .draggable-block:active {
  cursor: grabbing;
}

.gjs-blocks-c, #blocks-basic, #blocks-layout, #blocks-media, #blocks-advanced {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    padding: 8px;
}



.gjs-block span {
  display: block;
  margin-top: 2px;
  font-size: 7px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  opacity: 0.8;
}

.gjs-block:hover, .draggable-block:hover {
  background: linear-gradient(135deg, #4b5563 0%, #6b7280 100%);
  transform: translateY(-2px) scale(1.02);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  border-color: #60a5fa;
  border-style: solid;
  color: #ffffff;
}

.gjs-block:hover span {
  opacity: 1;
}

.gjs-block:hover::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(96, 165, 250, 0.2), transparent);
  transition: left 0.6s ease;
  left: 100%;
}

.gjs-block::after {
  content: 'Click or drag to add';
  position: absolute;
  bottom: -20px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 8px;
  color: #9ca3af;
  opacity: 0;
  transition: opacity 0.3s ease;
  white-space: nowrap;
  pointer-events: none;
}

.gjs-block:hover::after {
  opacity: 1;
}

/* Image upload placeholder styles */
.image-upload-placeholder {
  transition: all 0.3s ease;
  position: relative;
}

.image-upload-placeholder:hover {
  border-color: #0056b3 !important;
  background: #e3f2fd !important;
  transform: scale(1.02);
}

.image-upload-placeholder:active {
  transform: scale(0.98);
}

/* Upload loading animation */
@keyframes uploadPulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.uploading {
  animation: uploadPulse 1.5s infinite;
}

/* Drop zone indicator */
.gjs-cv-canvas.drag-over {
  background: rgba(0, 123, 255, 0.1) !important;
  border: 2px dashed #007bff !important;
}

.gjs-block:active {
  transform: translateY(0);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
}

.save-btn {
  min-width: 100px;
  font-size: 10px;
  font-weight: 600;

  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
  border: 1px solid rgba(16, 185, 129, 0.3) !important;
  box-shadow: 0 2px 6px rgba(16, 185, 129, 0.3) !important;
  color: white !important;
  border-radius: 3px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  position: relative !important;
  overflow: hidden !important;
  padding: 4px 12px !important;
  height: 24px !important;
}

.save-btn:hover {
  background: linear-gradient(135deg, #059669 0%, #047857 100%) !important;
  transform: translateY(-2px) scale(1.05) !important;
  box-shadow: 0 6px 20px rgba(16, 185, 129, 0.4) !important;
  border-color: rgba(16, 185, 129, 0.5) !important;
}

.save-btn:active {
  transform: translateY(0) scale(0.98) !important;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3) !important;
}

.save-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.6s ease;
}

.save-btn:hover::before {
  left: 100%;
}

.toolbar-btn:hover {
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  transform: translateY(-1px) scale(1.05);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.4);
}

.toolbar-btn:hover::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s;
  left: 100%;
}

.toolbar-btn.active {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  box-shadow: 0 0 0 1px rgba(16, 185, 129, 0.3), 0 2px 4px rgba(0, 0, 0, 0.3);
  transform: scale(1.05);
}

.toolbar-btn:active {
  transform: translateY(0) scale(0.95);
  transition: all 0.1s ease;
}

.device-select:hover {
  background: linear-gradient(135deg, #9ca3af 0%, #d1d5db 100%);
  color: #1f2937;
}

#gjs-canvas {
  height: 100% !important;
  width: 100% !important;
  background: #0f1419;
  min-height: 100% !important;
  min-width: 100% !important;
  margin: 0 !important;
  padding: 0 !important;
  top: 0 !important;
  left: 0 !important;
}

#gjs {
  height: 100% !important;
  width: 100% !important;
  min-height: 100% !important;
  min-width: 100% !important;
  margin: 0 !important;
  padding: 0 !important;
  top: 0 !important;
  left: 0 !important;
}

.gjs-cv-canvas {
  width: 100% !important;
  height: 100% !important;
  min-width: 100% !important;
  min-height: 100% !important;
  margin: 0 !important;
  padding: 0 !important;
  top: 0 !important;
  left: 0 !important;
}

.gjs-frame {
  width: 100% !important;
  height: 100% !important;
  min-width: 100% !important;
  min-height: 100% !important;
  margin: 0 !important;
  padding: 0 !important;
  top: 0 !important;
  left: 0 !important;
  border: none !important;
}

.gjs-cv-canvas iframe {
  margin: 0 !important;
  padding: 0 !important;
  top: 0 !important;
  left: 0 !important;
  border: none !important;
}

.gjs-title{
padding:0 10px !important;
font-size:14px !important;
}
.gjs-sm-sector-title{
padding:0 10px !important;
font-size:14px !important;
}

/* Custom scrollbar for dark theme */
.editor-left-panel::-webkit-scrollbar,
.editor-right-panel::-webkit-scrollbar,
.elements-container::-webkit-scrollbar,
.right-panel-content::-webkit-scrollbar {
  width: 6px;
}

.editor-left-panel::-webkit-scrollbar-track,

.editor-right-panel::-webkit-scrollbar-track,
.elements-container::-webkit-scrollbar-track,
.right-panel-content::-webkit-scrollbar-track {
  background: #1a1a1a;
  border-radius: 3px;
}

.editor-left-panel::-webkit-scrollbar-thumb,
.editor-right-panel::-webkit-scrollbar-thumb,
.elements-container::-webkit-scrollbar-thumb,
.right-panel-content::-webkit-scrollbar-thumb {
  background: #4b5563;
  border-radius: 3px;
}

.editor-left-panel::-webkit-scrollbar-thumb:hover,
.editor-right-panel::-webkit-scrollbar-thumb:hover,
.elements-container::-webkit-scrollbar-thumb:hover,
.right-panel-content::-webkit-scrollbar-thumb:hover {
  background: #6b7280;

}

/* Enhanced save button styling */
.save-btn {
  animation: savePulse 2s infinite;
}

@keyframes savePulse {
  0%, 100% {
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
  }
  50% {
    box-shadow: 0 6px 20px rgba(16, 185, 129, 0.4);
  }
}

/* Dark theme for GrapesJS components */
.gjs-cv-canvas {
  background: #0f1419 !important;
}

.gjs-frame {
  border: 1px solid #374151 !important;
}

/* Center canvas content for tablet and mobile views */
.gjs-cv-canvas {
  display: flex !important;
  justify-content: center !important;
  align-items: flex-start !important;
}

.gjs-frame {
  margin: 0 auto !important;
  display: block !important;
}

/* Specific centering for device preview modes */
.gjs-device-tablet .gjs-cv-canvas,
.gjs-device-mobile .gjs-cv-canvas {
  justify-content: center !important;
  padding: 20px !important;
}

.gjs-device-tablet .gjs-frame,
.gjs-device-mobile .gjs-frame {
  margin: 0 auto !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3) !important;
  border-radius: 8px !important;
  overflow: hidden !important;
}

/* Ensure iframe content is centered */
.gjs-cv-canvas iframe {
  display: block !important;
  margin: 0 auto !important;
}

/* Device-specific body classes for additional styling */
body.device-tablet .gjs-cv-canvas,
body.device-mobile .gjs-cv-canvas {
  display: flex !important;
  justify-content: center !important;
  align-items: flex-start !important;
  padding: 20px !important;
  background: #1a1a1a !important;
}

body.device-tablet .gjs-frame,
body.device-mobile .gjs-frame {
  margin: 0 auto !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3) !important;
  border-radius: 8px !important;
  overflow: hidden !important;
  background: white !important;
}

/* Additional centering for content inside iframe */
body.device-tablet .gjs-cv-canvas iframe,
body.device-mobile .gjs-cv-canvas iframe {
  margin: 0 auto !important;
  display: block !important;
}
</style>

${jsContent ? `<script>
/* === PROJECT JS FILES === */
${jsContent}
/* === END PROJECT JS === */
</script>` : ''}

<script type="text/javascript">
// Store the project CSS content
const PROJECT_CSS_CONTENT = \`${escapedCssContent}\`;
const PROJECT_JS_CONTENT = \`${escapedJsContent}\`;

// Global function to handle content changes (will be set by parent component)
window.onGrapesJSChange = window.onGrapesJSChange || function(htmlContent) {
  console.log('GrapesJS content changed:', htmlContent);
};

// Global function to handle save (will be set by parent component)
window.onGrapesJSSave = window.onGrapesJSSave || function(htmlContent, cssContent) {
  console.log('GrapesJS content saved:', htmlContent);
  console.log('GrapesJS CSS saved:', cssContent);
};

// Global function to handle image upload (will be set by parent component)
window.onImageUpload = window.onImageUpload || async function(file) {
  console.log('Image upload requested:', file);
  
  // Use postMessage to communicate with parent
  try {
    const response = await new Promise((resolve, reject) => {
      const messageId = Date.now().toString();
      
      // Set up response listener
      const handleResponse = (event) => {
        if (event.data && event.data.type === 'IMAGE_UPLOAD_RESPONSE' && event.data.messageId === messageId) {
          window.removeEventListener('message', handleResponse);
          if (event.data.success) {
            resolve(event.data.imageUrl);
          } else {
            reject(new Error(event.data.error || 'Upload failed'));
          }
        }
      };
      
      window.addEventListener('message', handleResponse);
      
      // Send upload request to parent
      window.parent.postMessage({
        type: 'IMAGE_UPLOAD_REQUEST',
        messageId: messageId,
        file: {
          name: file.name,
          type: file.type,
          size: file.size
        }
      }, '*');
      
      // Convert file to base64 for transfer
      const reader = new FileReader();
      reader.onload = () => {
        window.parent.postMessage({
          type: 'IMAGE_UPLOAD_DATA',
          messageId: messageId,
          data: reader.result
        }, '*');
      };
      reader.readAsDataURL(file);
      
      // Timeout after 30 seconds
      setTimeout(() => {
        window.removeEventListener('message', handleResponse);
        reject(new Error('Upload timeout'));
      }, 30000);
    });
    
    return response;
  } catch (error) {
    console.error('Image upload failed:', error);
    return null;
  }
};

// Track if there are unsaved changes
let hasUnsavedChanges = false;


// Utility function to handle image upload
async function uploadImage(file) {
  try {
    // Call the global upload function
    const imageUrl = await window.onImageUpload(file);
    return imageUrl;
  } catch (error) {
    console.error('Image upload failed:', error);
    return null;
  }
}

// Utility function to extract clean HTML
function extractCleanHtml(editor) {
  try {
    const html = editor.getHtml();
    const css = editor.getCss();
    
    // Get Tailwind CSS if the plugin is available
    let tailwindCss = '';
    try {
      if (editor.runCommand && editor.runCommand('get-tailwindCss')) {
        tailwindCss = editor.runCommand('get-tailwindCss');
      }
    } catch (e) {
      console.log('Tailwind CSS generation not available:', e);
    }
    
    // Remove GrapesJS specific attributes and classes, but preserve event handlers
    let cleanHtml = html
      .replace(/data-gjs-[^=]*="[^"]*"/g, '') // Remove data-gjs-* attributes
      .replace(/class="[^"]*gjs[^"]*"/g, '') // Remove gjs classes
      .replace(/\\s+class=""/g, '') // Remove empty class attributes
      .replace(/\\s+/g, ' ') // Normalize whitespace
      .trim();
    
    // Always wrap in a complete HTML document with project CSS and Tailwind
    return \`<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>Page</title>
<link href="https://cdn.jsdelivr.net/npm/tailwindcss@2.2.19/dist/tailwind.min.css" rel="stylesheet">
<style>
/* === PROJECT CSS FILES === */
\${PROJECT_CSS_CONTENT}
/* === END PROJECT CSS === */

/* === TAILWIND GENERATED CSS === */
\${tailwindCss}
/* === END TAILWIND CSS === */

/* === EDITOR GENERATED CSS === */
\${css}
/* === END EDITOR CSS === */
</style>
</head>
<body>
\${cleanHtml}
</body>
</html>\`;
  } catch (error) {
    console.error('Error extracting clean HTML:', error);
    return '';
  }
}

// Function to show/hide save button
function toggleSaveButton(show) {
  const saveBtn = document.getElementById('btn-save');
  if (saveBtn) {
    saveBtn.style.display = show ? 'inline-block' : 'none';
  }
  hasUnsavedChanges = show;
}

// Debounce function to prevent too many updates
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Function to inject project CSS into canvas
function injectProjectCSS() {
  console.log('Injecting project CSS into canvas, length:', PROJECT_CSS_CONTENT.length);
  
  // Get the canvas iframe
  const canvasFrame = document.querySelector('#gjs iframe');
  if (canvasFrame && canvasFrame.contentDocument) {
    const canvasHead = canvasFrame.contentDocument.head;
    
    // Remove existing project styles
    const existingProjectStyle = canvasFrame.contentDocument.getElementById('injected-project-styles');
    if (existingProjectStyle) {
      existingProjectStyle.remove();
    }
    
    // Remove existing tailwind styles
    const existingTailwindStyle = canvasFrame.contentDocument.getElementById('injected-tailwind-styles');
    if (existingTailwindStyle) {
      existingTailwindStyle.remove();
    }
    
    // Inject Tailwind CSS first (same as what will be saved)
    const tailwindLink = canvasFrame.contentDocument.createElement('link');
    tailwindLink.id = 'injected-tailwind-styles';
    tailwindLink.rel = 'stylesheet';
    tailwindLink.href = 'https://cdn.jsdelivr.net/npm/tailwindcss@2.2.19/dist/tailwind.min.css';
    canvasHead.appendChild(tailwindLink);
    
    // Inject project CSS after Tailwind (higher priority)
    if (PROJECT_CSS_CONTENT && PROJECT_CSS_CONTENT.trim()) {
      const styleElement = canvasFrame.contentDocument.createElement('style');
      styleElement.id = 'injected-project-styles';
      styleElement.textContent = \`
/* === ALL PROJECT CSS FILES INJECTED === */
\${PROJECT_CSS_CONTENT}
/* === END PROJECT CSS === */
      \`;
      
      // Insert at the END of head to ensure highest priority
      canvasHead.appendChild(styleElement);
      
      console.log('All project CSS files injected successfully into canvas');
      console.log('CSS content includes:', PROJECT_CSS_CONTENT.split('/* ===').length - 1, 'CSS files');
    }
    
    // Also inject original page styles from the parent document
    const originalStyles = document.querySelectorAll('style, link[rel="stylesheet"]');
    originalStyles.forEach(style => {
      if (style.id !== 'project-styles') { // Don't duplicate project styles
        const clonedStyle = style.cloneNode(true);
        canvasHead.appendChild(clonedStyle);
      }
    });
    
    // Ensure the canvas body preserves original styling
    const canvasBody = canvasFrame.contentDocument.body;
    if (canvasBody) {
      // Preserve original body classes and attributes
      const originalBody = document.body;
      if (originalBody.className) {
        canvasBody.className = originalBody.className;
      }
      if (originalBody.id) {
        canvasBody.id = originalBody.id;
      }
      
      // Copy all data attributes
      Array.from(originalBody.attributes).forEach(attr => {
        if (attr.name.startsWith('data-')) {
          canvasBody.setAttribute(attr.name, attr.value);
        }
      });
    }
    
    console.log('Tailwind CSS, Project CSS, and Original Page CSS injected successfully into canvas');
  } else {
    console.warn('Canvas iframe not found or not ready');
  }
}

const editor = grapesjs.init({
  container: '#gjs',
  fromElement: true,
  height: '100%',
  width: '100%',
  plugins: ['grapesjs-tailwind'],
  
  // Preserve original page styling
  avoidDefaults: true,
  noticeOnUnload: 0,
  
  // Parser configuration to allow event handlers and scripts
  parser: {
    optionsHtml: {
      allowScripts: true,
      allowUnsafeAttr: true,
      allowUnsafeAttrValue: true,
      keepEmptyTextNodes: true
    }
  },
  
  // Ensure proper styling inheritance
  wrapper: {
    component: 'body',
    style: {
      'background': 'transparent',
      'margin': '0',
      'padding': '0'
    }
  },
  
  // Storage configuration - DISABLE autoload to prevent override
  storageManager: {
    type: 'local',
    autosave: false,
    autoload: false,
    stepsBeforeSave: 5,
  },
  
  // Canvas configuration - Preserve original page styling
   canvas: {
    styles: [
      'https://cdn.jsdelivr.net/npm/tailwindcss@2.2.19/dist/tailwind.min.css',
      'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/css/all.min.css'
    ],
    scripts: [],
    width: '100%',
    height: '100%',
    // Preserve original page styling
    customBadgeLabel: false,
    // Ensure proper styling inheritance
    stylesInWrapper: true
  },
  
  // Device manager for responsive design
  deviceManager: {
    devices: [
      {
        name: 'Desktop',
        width: '',
      },
      {
        name: 'Tablet',
        width: '768px',
      },
      {
        name: 'Mobile',
        width: '320px',
      }
    ]
  },
  
  // Panels configuration
  panels: {
    defaults: []
  },
  
  // Block manager configuration - temporarily disable to customize later
  blockManager: {
    appendTo: '#blocks-basic', // Will be dynamically changed
    blocks: [] // Will be populated dynamically
  },
  

  
  // Style manager
  styleManager: {
    appendTo: '#styles-container',
    sectors: [
      {
        name: 'Layout',
        open: true,
        buildProps: ['display', 'position', 'float', 'top', 'right', 'bottom', 'left', 'z-index'],
      },
      {
        name: 'Dimensions',
        open: true,
        buildProps: ['width', 'height', 'min-width', 'min-height', 'max-width', 'max-height', 'margin', 'padding'],
      },
      {
        name: 'Typography',
        open: false,
        buildProps: ['font-family', 'font-size', 'font-weight', 'line-height', 'letter-spacing', 'color', 'text-align', 'text-decoration', 'text-transform'],
      },
      {
        name: 'Background',
        open: false,
        buildProps: ['background-color', 'background-image', 'background-size', 'background-position', 'background-repeat'],
      },
      {
        name: 'Border',
        open: false,
        buildProps: ['border', 'border-width', 'border-style', 'border-color', 'border-radius'],
      },
      {
        name: 'Effects',
        open: false,
        buildProps: ['opacity', 'box-shadow', 'transform', 'transition', 'filter'],
      },
      {
        name: 'Flexbox',
        open: false,
        buildProps: ['flex-direction', 'justify-content', 'align-items', 'align-content', 'flex-wrap', 'flex-grow', 'flex-shrink', 'flex-basis'],
      }
    ]
  },
  
  // Trait manager
  traitManager: {
    appendTo: '#traits-container',
  },
  
  // Tailwind plugin configuration
  pluginsOpts: {
    'grapesjs-tailwind': {
      // Tailwind configuration options
      config: {
        content: [],
        theme: {
          extend: {},
        },
        plugins: [],
      },
      // Custom CSS classes to include
      customClasses: [],
      // Whether to show utility classes in the style manager
      showUtilityClasses: true,
      // Whether to show component classes in the style manager
      showComponentClasses: true,
    }
  }
});

// Allow all components to edit classes and common attributes
editor.on('component:selected', (component) => {
  const traits = component.get('traits');
  
  // Add class trait if not present
  if (!traits.some(trait => trait.get('name') === 'class')) {
    component.addTrait({
      type: 'text',
      label: 'CSS Classes',
      name: 'class',
      changeProp: 1
    });
  }
  
  // Add ID trait if not present
  if (!traits.some(trait => trait.get('name') === 'id')) {
    component.addTrait({
      type: 'text',
      label: 'Element ID',
      name: 'id',
      changeProp: 1
    });
  }
  
  // Add specific traits based on component type
  const tagName = component.get('tagName');
  
  if (tagName === 'img' && !traits.some(trait => trait.get('name') === 'alt')) {
    component.addTrait({
      type: 'text',
      label: 'Alt Text',
      name: 'alt',
      changeProp: 1
    });
  }
  
  if (tagName === 'a' && !traits.some(trait => trait.get('name') === 'href')) {
    component.addTrait({
      type: 'text',
      label: 'Link URL',
      name: 'href',
      changeProp: 1
    });
    
    component.addTrait({
      type: 'select',
      label: 'Open In',
      name: 'target',
      options: [
        { value: '', name: 'Same Window' },
        { value: '_blank', name: 'New Window' }
      ],
      changeProp: 1
    });
  }
  
  if (tagName === 'button' && !traits.some(trait => trait.get('name') === 'type')) {
    component.addTrait({
      type: 'select',
      label: 'Button Type',
      name: 'type',
      options: [
        { value: 'button', name: 'Button' },
        { value: 'submit', name: 'Submit' },
        { value: 'reset', name: 'Reset' }
      ],
      changeProp: 1
    });
  }
});

// Debounced function to handle content changes
const debouncedOnChange = debounce(() => {
  // Show save button when there are changes
  toggleSaveButton(true);
}, 1000); // Debounce for 1 second

// Function to handle save
function handleSave() {
  const cleanHtml = extractCleanHtml(editor);
  const editorCss = editor.getCss();
  
  // Get Tailwind CSS if available
  let tailwindCss = '';
  try {
    if (editor.runCommand && editor.runCommand('get-tailwindCss')) {
      tailwindCss = editor.runCommand('get-tailwindCss');
    }
  } catch (e) {
    console.log('Tailwind CSS generation not available:', e);
  }
  
  // Combine all CSS
  const combinedCss = \`\${tailwindCss ? \`/* === TAILWIND GENERATED CSS === */
\${tailwindCss}
/* === END TAILWIND CSS === */

\` : ''}\${editorCss}\`;
  
  // Use postMessage to communicate with parent window
  try {
    window.parent.postMessage({
      type: 'GRAPESJS_SAVE',
      htmlContent: cleanHtml,
      cssContent: combinedCss
    }, '*');
  } catch (error) {
    // Fallback to direct function call if postMessage fails
    if (window.onGrapesJSSave) {
      window.onGrapesJSSave(cleanHtml, combinedCss);
    }
  }
  
  // Hide save button after saving
  toggleSaveButton(false);
}

// Toolbar event handlers
document.getElementById('btn-undo').addEventListener('click', () => {
  editor.UndoManager.undo();
  debouncedOnChange(); // Trigger change after undo
});

document.getElementById('btn-redo').addEventListener('click', () => {
  editor.UndoManager.redo();
  debouncedOnChange(); // Trigger change after redo
});

// Save button event handler
document.getElementById('btn-save').addEventListener('click', handleSave);

// Device selector
document.getElementById('device-selector').addEventListener('change', (e) => {
  const selectedDevice = e.target.value;
  editor.setDevice(selectedDevice);
  
  // Add custom class to body for device-specific styling
  const editorBody = document.body;
  // Remove existing device classes
  editorBody.classList.remove('device-desktop', 'device-tablet', 'device-mobile');
  
  // Add new device class
  editorBody.classList.add('device-' + selectedDevice.toLowerCase());
  
  // Center the canvas content for tablet and mobile
  setTimeout(() => {
    const canvas = document.querySelector('.gjs-cv-canvas');
    const frame = document.querySelector('.gjs-frame');
    
    if (selectedDevice === 'Tablet' || selectedDevice === 'Mobile') {
      if (canvas) {
        canvas.style.display = 'flex';
        canvas.style.justifyContent = 'center';
        canvas.style.alignItems = 'flex-start';
        canvas.style.padding = '20px';
      }
      if (frame) {
        frame.style.margin = '0 auto';
        frame.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.3)';
        frame.style.borderRadius = '8px';
        frame.style.overflow = 'hidden';
      }
    } else {
      // Reset for desktop
      if (canvas) {
        canvas.style.display = 'block';
        canvas.style.justifyContent = '';
        canvas.style.alignItems = '';
        canvas.style.padding = '0';
      }
      if (frame) {
        frame.style.margin = '0';
        frame.style.boxShadow = '';
        frame.style.borderRadius = '';
        frame.style.overflow = '';
      }
    }
  }, 100);
});

// Block definitions organized by category
const blockCategories = {
  // basic: [
  //   {
  //     id: 'text',
  //     label: 'Text',
  //     content: '<div data-gjs-type="text" style="padding: 10px; font-family: inherit;">Insert your text here</div>',
  //   },
  //   {
  //     id: 'heading',
  //     label: 'Heading',
  //     content: '<h2 style="margin: 20px 0; color: #333; font-family: inherit; font-weight: 600;">Heading Text</h2>',
  //   },
  //   {
  //     id: 'paragraph',
  //     label: 'Paragraph',
  //     content: '<p style="margin: 16px 0; line-height: 1.6; color: #666; font-family: inherit;">This is a paragraph of text. You can edit this content to add your own text.</p>',
  //   },
  //   {
  //     id: 'link',
  //     label: 'Link',
  //     content: '<a href="#" style="color: #007bff; text-decoration: underline; margin: 10px; font-family: inherit;">Link Text</a>',
  //   },
  //   {
  //     id: 'button',
  //     label: 'Button',
  //     content: '<button style="padding: 12px 24px; background: #007bff; color: white; border: none; border-radius: 6px; cursor: pointer; margin: 10px; font-weight: 500; transition: all 0.3s;">Click me</button>',
  //   },
  //   {
  //     id: 'list',
  //     label: 'List',
  //     content: '<ul style="margin: 16px 0; padding-left: 20px; color: #666; font-family: inherit;"><li>First item</li><li>Second item</li><li>Third item</li></ul>',
  //   },
  //   {
  //     id: 'divider',
  //     label: 'Divider',
  //     content: '<hr style="margin: 30px 0; border: none; border-top: 1px solid #e0e0e0;">',
  //   }
  // ],
  // layout: [
  //   {
  //     id: 'section',
  //     label: 'Section',
  //     content: '<section style="padding: 40px 20px; margin: 20px 0; background: #f8f9fa; border: 2px dashed #dee2e6; border-radius: 8px; text-align: center;"><h2 style="margin: 0 0 16px 0; color: #495057; font-size: 24px;">New Section</h2><p style="margin: 0; color: #6c757d;">This is a new section. Click to edit the content.</p></section>',
  //   },
  //   {
  //     id: 'container',
  //     label: 'Container',
  //     content: '<div style="padding: 30px; margin: 20px 0; border: 2px dashed #dee2e6; min-height: 120px; background: #ffffff; border-radius: 8px; text-align: center; color: #6c757d;"><p style="margin: 0;">Drop elements here</p></div>',
  //   },
  //   {
  //     id: 'columns',
  //     label: 'Columns',
  //     content: '<div style="display: flex; margin: 20px 0; gap: 20px;"><div style="flex: 1; padding: 30px; background: #f8f9fa; border: 2px dashed #dee2e6; min-height: 120px; border-radius: 8px; text-align: center; color: #6c757d;">Column 1</div><div style="flex: 1; padding: 30px; background: #f8f9fa; border: 2px dashed #dee2e6; min-height: 120px; border-radius: 8px; text-align: center; color: #6c757d;">Column 2</div></div>',
  //   },
  //   {
  //     id: 'three-columns',
  //     label: '3 Columns',
  //     content: '<div style="display: flex; margin: 20px 0; gap: 16px;"><div style="flex: 1; padding: 20px; background: #f8f9fa; border: 2px dashed #dee2e6; min-height: 100px; border-radius: 8px; text-align: center; color: #6c757d; font-size: 14px;">Column 1</div><div style="flex: 1; padding: 20px; background: #f8f9fa; border: 2px dashed #dee2e6; min-height: 100px; border-radius: 8px; text-align: center; color: #6c757d; font-size: 14px;">Column 2</div><div style="flex: 1; padding: 20px; background: #f8f9fa; border: 2px dashed #dee2e6; min-height: 100px; border-radius: 8px; text-align: center; color: #6c757d; font-size: 14px;">Column 3</div></div>',
  //   },
  //   {
  //     id: 'card',
  //     label: 'Card',
  //     content: '<div style="background: white; border-radius: 12px; box-shadow: 0 4px 12px rgba(0,0,0,0.1); padding: 24px; margin: 20px 0; border: 1px solid #e0e0e0;"><h3 style="margin: 0 0 12px 0; color: #333; font-size: 20px;">Card Title</h3><p style="margin: 0; color: #666; line-height: 1.5;">This is a card component. You can add any content here.</p></div>',
  //   },
  //   {
  //     id: 'sidebar',
  //     label: 'Sidebar Layout',
  //     content: '<div style="display: flex; margin: 20px 0; gap: 20px;"><div style="width: 250px; padding: 20px; background: #f8f9fa; border: 2px dashed #dee2e6; border-radius: 8px; text-align: center; color: #6c757d;">Sidebar</div><div style="flex: 1; padding: 20px; background: #ffffff; border: 2px dashed #dee2e6; border-radius: 8px; text-align: center; color: #6c757d;">Main Content</div></div>',
  //   }
  // ],
  media: [
    {
      id: 'image-upload',
      label: 'Upload Image',
      content: '<div class="image-upload-placeholder" style="border: 2px dashed #007bff; border-radius: 8px; padding: 40px; text-align: center; background: #f8f9fa; margin: 20px 0; cursor: pointer;"><div style="font-size: 48px; color: #007bff; margin-bottom: 10px;" class="image-upload-placeholder">📷</div><div style="color: #6c757d; font-size: 14px;" class="image-upload-placeholder">Click to upload image</div></div>',
    },
    // {
    //   id: 'image-assets',
    //   label: 'Project Assets',
    //   content: '<div class="image-assets-placeholder" style="border: 2px dashed #28a745; border-radius: 8px; padding: 40px; text-align: center; background: #f8fff9; margin: 20px 0; cursor: pointer;"><div style="font-size: 48px; color: #28a745; margin-bottom: 10px;">📁</div><div style="color: #6c757d; font-size: 14px;">Click to select from project assets</div></div>',
    // },
    // {
    //   id: 'video',
    //   label: 'Video',
    //   content: '<div style="margin: 20px; text-align: center; background: #f8f9fa; padding: 20px; border-radius: 8px;"><iframe width="400" height="225" src="https://www.youtube.com/embed/dQw4w9WgXcQ" frameborder="0" allowfullscreen style="max-width: 100%; border-radius: 8px; box-shadow: 0 4px 8px rgba(0,0,0,0.1);"></iframe></div>',
    // },
    // {
    //   id: 'image-gallery',
    //   label: 'Image Gallery',
    //   content: '<div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 16px; padding: 20px;"><div class="image-upload-placeholder" style="border: 2px dashed #007bff; border-radius: 8px; padding: 20px; text-align: center; background: #f8f9fa; cursor: pointer; min-height: 150px; display: flex; align-items: center; justify-content: center;"><div><div style="font-size: 24px; color: #007bff; margin-bottom: 5px;">📷</div><div style="color: #6c757d; font-size: 12px;">Upload Image</div></div></div><div class="image-upload-placeholder" style="border: 2px dashed #007bff; border-radius: 8px; padding: 20px; text-align: center; background: #f8f9fa; cursor: pointer; min-height: 150px; display: flex; align-items: center; justify-content: center;"><div><div style="font-size: 24px; color: #007bff; margin-bottom: 5px;">📷</div><div style="color: #6c757d; font-size: 12px;">Upload Image</div></div></div><div class="image-upload-placeholder" style="border: 2px dashed #007bff; border-radius: 8px; padding: 20px; text-align: center; background: #f8f9fa; cursor: pointer; min-height: 150px; display: flex; align-items: center; justify-content: center;"><div><div style="font-size: 24px; color: #007bff; margin-bottom: 5px;">📷</div><div style="color: #6c757d; font-size: 12px;">Upload Image</div></div></div></div>',
    // },
    // {
    //   id: 'video-player',
    //   label: 'Video Player',
    //   content: '<video controls style="width: 100%; max-width: 500px; height: auto; border-radius: 8px; box-shadow: 0 4px 8px rgba(0,0,0,0.1); margin: 20px auto; display: block;"><source src="https://sample-videos.com/zip/10/mp4/SampleVideo_1280x720_1mb.mp4" type="video/mp4">Your browser does not support the video tag.</video>',
    // },
    // {
    //   id: 'audio-player',
    //   label: 'Audio Player',
    //   content: '<div style="margin: 20px; text-align: center; background: #f8f9fa; padding: 20px; border-radius: 8px;"><audio controls style="width: 100%; max-width: 400px;"><source src="https://sample-videos.com/zip/10/mp3/SampleAudio_0.4mb.mp3" type="audio/mpeg">Your browser does not support the audio tag.</audio></div>',
    // },
    // {
    //   id: 'video',
    //   label: 'Video',
    //   content: '<div style="margin: 20px; text-align: center; background: #f8f9fa; padding: 20px; border-radius: 8px;"><iframe width="400" height="225" src="https://www.youtube.com/embed/dQw4w9WgXcQ" frameborder="0" allowfullscreen style="max-width: 100%; border-radius: 8px; box-shadow: 0 4px 8px rgba(0,0,0,0.1);"></iframe></div>',
    // },
    // {
    //   id: 'image-gallery',
    //   label: 'Image Gallery',
    //   content: '<div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 16px; padding: 20px;"><div class="image-upload-placeholder" style="border: 2px dashed #007bff; border-radius: 8px; padding: 20px; text-align: center; background: #f8f9fa; cursor: pointer; min-height: 150px; display: flex; align-items: center; justify-content: center;"><div><div style="font-size: 24px; color: #007bff; margin-bottom: 5px;">📷</div><div style="color: #6c757d; font-size: 12px;">Upload Image</div></div></div><div class="image-upload-placeholder" style="border: 2px dashed #007bff; border-radius: 8px; padding: 20px; text-align: center; background: #f8f9fa; cursor: pointer; min-height: 150px; display: flex; align-items: center; justify-content: center;"><div><div style="font-size: 24px; color: #007bff; margin-bottom: 5px;">📷</div><div style="color: #6c757d; font-size: 12px;">Upload Image</div></div></div><div class="image-upload-placeholder" style="border: 2px dashed #007bff; border-radius: 8px; padding: 20px; text-align: center; background: #f8f9fa; cursor: pointer; min-height: 150px; display: flex; align-items: center; justify-content: center;"><div><div style="font-size: 24px; color: #007bff; margin-bottom: 5px;">📷</div><div style="color: #6c757d; font-size: 12px;">Upload Image</div></div></div></div>',
    // },
    // {
    //   id: 'video-player',
    //   label: 'Video Player',
    //   content: '<video controls style="width: 100%; max-width: 500px; height: auto; border-radius: 8px; box-shadow: 0 4px 8px rgba(0,0,0,0.1); margin: 20px auto; display: block;"><source src="https://sample-videos.com/zip/10/mp4/SampleVideo_1280x720_1mb.mp4" type="video/mp4">Your browser does not support the video tag.</video>',
    // },
    // {
    //   id: 'audio-player',
    //   label: 'Audio Player',
    //   content: '<div style="margin: 20px; text-align: center; background: #f8f9fa; padding: 20px; border-radius: 8px;"><audio controls style="width: 100%; max-width: 400px;"><source src="https://sample-videos.com/zip/10/mp3/SampleAudio_0.4mb.mp3" type="audio/mpeg">Your browser does not support the audio tag.</audio></div>',
    // },
    // {
    //   id: 'iframe-embed',
    //   label: 'Iframe Embed',
    //   content: '<iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3024.2219901290355!2d-74.00369368400567!3d40.71312937933185!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c25a23e28c1191%3A0x49f75d3281df052a!2s150%20Park%20Row%2C%20New%20York%2C%20NY%2010007!5e0!3m2!1sen!2sus!4v1644262070010!5m2!1sen!2sus" width="100%" height="300" style="border:0; border-radius: 8px; box-shadow: 0 4px 8px rgba(0,0,0,0.1); margin: 20px 0;" allowfullscreen="" loading="lazy"></iframe>',
    // }
  ],
  // advanced: [
  //   {
  //     id: 'tailwind-hero',
  //     label: 'Hero Section',
  //     content: '<div class="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-20 px-6"><div class="container mx-auto text-center"><h1 class="text-5xl font-bold mb-6">Welcome to Our Platform</h1><p class="text-xl mb-8 opacity-90">Build amazing experiences with modern design</p><button class="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition duration-300">Get Started</button></div></div>',
  //   },
  //   {
  //     id: 'tailwind-card',
  //     label: 'Feature Card',
  //     content: '<div class="max-w-sm bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"><img class="w-full h-48 object-cover" src="https://via.placeholder.com/300x200/4F46E5/ffffff?text=Feature" alt="Feature image"><div class="p-6"><h3 class="font-bold text-xl mb-2 text-gray-900">Feature Title</h3><p class="text-gray-600 leading-relaxed">Description of your amazing feature with compelling benefits.</p><div class="mt-4"><span class="inline-block bg-blue-100 text-blue-800 rounded-full px-3 py-1 text-sm font-semibold mr-2">#feature</span></div></div></div>',
  //   },
  //   {
  //     id: 'tailwind-button-group',
  //     label: 'Button Group',
  //     content: '<div class="flex flex-wrap gap-4 p-6"><button class="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg transition duration-300">Primary</button><button class="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded-lg transition duration-300">Secondary</button><button class="border-2 border-blue-500 text-blue-500 hover:bg-blue-500 hover:text-white px-6 py-2 rounded-lg transition duration-300">Outline</button></div>',
  //   },
  //   {
  //     id: 'tailwind-navbar',
  //     label: 'Navigation Bar',
  //     content: '<nav class="bg-white shadow-lg border-b border-gray-200"><div class="container mx-auto px-6 py-3"><div class="flex justify-between items-center"><div class="text-2xl font-bold text-gray-800">Brand</div><div class="hidden md:flex space-x-6"><a href="#" class="text-gray-600 hover:text-blue-600 transition duration-300">Home</a><a href="#" class="text-gray-600 hover:text-blue-600 transition duration-300">About</a><a href="#" class="text-gray-600 hover:text-blue-600 transition duration-300">Services</a><a href="#" class="text-gray-600 hover:text-blue-600 transition duration-300">Contact</a></div><button class="md:hidden text-gray-600">☰</button></div></div></nav>',
  //   },
  //   {
  //     id: 'tailwind-grid',
  //     label: 'Feature Grid',
  //     content: '<div class="grid grid-cols-1 md:grid-cols-3 gap-8 p-8 bg-gray-50"><div class="text-center p-6 bg-white rounded-lg shadow-md"><div class="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4"><span class="text-2xl">🚀</span></div><h3 class="text-xl font-semibold mb-2">Fast</h3><p class="text-gray-600">Lightning fast performance</p></div><div class="text-center p-6 bg-white rounded-lg shadow-md"><div class="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4"><span class="text-2xl">🔒</span></div><h3 class="text-xl font-semibold mb-2">Secure</h3><p class="text-gray-600">Enterprise-grade security</p></div><div class="text-center p-6 bg-white rounded-lg shadow-md"><div class="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4"><span class="text-2xl">⚡</span></div><h3 class="text-xl font-semibold mb-2">Reliable</h3><p class="text-gray-600">99.9% uptime guarantee</p></div></div>',
  //   },
  //   {
  //     id: 'tailwind-form',
  //     label: 'Contact Form',
  //     content: '<div class="max-w-md mx-auto bg-white p-8 rounded-lg shadow-lg"><h2 class="text-2xl font-bold mb-6 text-gray-800">Contact Us</h2><form class="space-y-4"><div><label class="block text-sm font-medium text-gray-700 mb-1">Name</label><input type="text" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Your name"></div><div><label class="block text-sm font-medium text-gray-700 mb-1">Email</label><input type="email" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="your@email.com"></div><div><label class="block text-sm font-medium text-gray-700 mb-1">Message</label><textarea rows="4" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Your message"></textarea></div><button type="submit" class="w-full bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-md transition duration-300">Send Message</button></form></div>',
  //   },
  //   {
  //     id: 'tailwind-testimonial',
  //     label: 'Testimonial',
  //     content: '<div class="bg-gray-100 p-8 rounded-lg max-w-2xl mx-auto"><div class="text-center"><img class="w-16 h-16 rounded-full mx-auto mb-4" src="https://via.placeholder.com/64x64/4F46E5/ffffff?text=JS" alt="User"><blockquote class="text-lg text-gray-700 mb-4 italic">"This platform has completely transformed how we work. The results speak for themselves."</blockquote><div class="font-semibold text-gray-900">John Smith</div><div class="text-gray-600 text-sm">CEO, Tech Company</div></div></div>',
  //   },
  //   {
  //     id: 'tailwind-pricing',
  //     label: 'Pricing Card',
  //     content: '<div class="max-w-sm bg-white rounded-xl shadow-lg border border-gray-200 p-6"><div class="text-center"><h3 class="text-2xl font-bold text-gray-900 mb-2">Pro Plan</h3><div class="text-4xl font-bold text-blue-600 mb-4">$29<span class="text-sm text-gray-500 font-normal">/month</span></div><ul class="space-y-3 mb-6"><li class="flex items-center"><span class="text-green-500 mr-2">✓</span>Unlimited projects</li><li class="flex items-center"><span class="text-green-500 mr-2">✓</span>Priority support</li><li class="flex items-center"><span class="text-green-500 mr-2">✓</span>Advanced analytics</li><li class="flex items-center"><span class="text-green-500 mr-2">✓</span>Custom integrations</li></ul><button class="w-full bg-blue-500 hover:bg-blue-600 text-white py-3 px-4 rounded-lg transition duration-300 font-semibold">Choose Plan</button></div></div>',
  //   }
  // ]
};

// Function to setup categorized blocks
function setupCategorizedBlocks() {
  const blockManager = editor.BlockManager;
  
  // Clear existing blocks
  blockManager.getAll().reset();
  
  // Add blocks for each category
  Object.keys(blockCategories).forEach(categoryKey => {
    blockCategories[categoryKey].forEach(block => {
      blockManager.add(block.id, {
        label: block.label,
        content: block.content,
        category: categoryKey
      });
    });
  });
  
  // Setup tab switching functionality
  const tabBtns = document.querySelectorAll('.tab-btn');
  const elementCategories = document.querySelectorAll('.element-category');
  
  tabBtns.forEach(btn => {
    btn.addEventListener('click', (e) => {
      const category = e.target.dataset.category;
      
      // Update active tab
      tabBtns.forEach(b => b.classList.remove('active'));
      e.target.classList.add('active');
      
      // Update active category
      elementCategories.forEach(c => c.classList.remove('active'));
      document.getElementById(\`category-\${category}\`).classList.add('active');
      
      // Update block manager container and re-render blocks for this category
      blockManager.appendTo = \`#blocks-\${category}\`;
      blockManager.render();
      
      // Filter blocks to show only current category
      const currentCategoryBlocks = blockCategories[category] || [];
      const blocksContainer = document.querySelector(\`#blocks-\${category}\`);
      
      if (blocksContainer) {
        // Clear existing blocks in this container
        blocksContainer.innerHTML = '';
        
        // Add blocks for current category
        currentCategoryBlocks.forEach(blockDef => {
          const block = blockManager.get(blockDef.id);
          if (block) {
            const blockEl = document.createElement('div');
            blockEl.className = 'gjs-block draggable-block';
            blockEl.innerHTML = \`
              <span>\${blockDef.label}</span>
            \`;
            blockEl.draggable = true;
            
            // Add drag functionality using GrapesJS native drag
            blockEl.addEventListener('dragstart', (e) => {
              e.dataTransfer.setData('text/plain', blockDef.id);
              e.dataTransfer.effectAllowed = 'copy';
              
              // Use GrapesJS native drag method
              try {
              editor.BlockManager.drag(blockDef.id);
              } catch (error) {
                console.log('Using fallback drag method');
                // Fallback: manually trigger block drag
                const blockModel = blockManager.get(blockDef.id);
                if (blockModel) {
                  editor.BlockManager.startDrag(blockModel);
                }
              }
            });
            
            // Add click functionality as fallback
            blockEl.addEventListener('click', () => {
              try {
              const selected = editor.getSelected();
              if (selected) {
                selected.append(blockDef.content);
              } else {
                editor.DomComponents.getWrapper().append(blockDef.content);
                }
                console.log('Block added via click:', blockDef.label);
              } catch (error) {
                console.error('Error adding block via click:', error);
              }
            });
            
            // Add double-click functionality for easier insertion
            blockEl.addEventListener('dblclick', () => {
              try {
                // Always add to wrapper for double-click
                editor.DomComponents.getWrapper().append(blockDef.content);
                console.log('Block added via double-click:', blockDef.label);
              } catch (error) {
                console.error('Error adding block via double-click:', error);
              }
            });
            
            // Add a more robust insertion method
            const insertBlock = () => {
              try {
                // Get the wrapper component
                const wrapper = editor.DomComponents.getWrapper();
                if (!wrapper) {
                  console.error('No wrapper found');
                  return;
                }
                
                // Create component from content
                const component = editor.DomComponents.addComponent(blockDef.content);
                
                // Add to wrapper
                wrapper.append(component);
                
                // Select the new component
                editor.select(component);
                
                console.log('Block inserted successfully:', blockDef.label);
                
                // Trigger change event
                debouncedOnChange();
                
              } catch (error) {
                console.error('Error inserting block:', error);
                // Fallback to simple append
                try {
                  const selected = editor.getSelected();
                  if (selected) {
                    selected.append(blockDef.content);
                  } else {
                    editor.DomComponents.getWrapper().append(blockDef.content);
                  }
                } catch (fallbackError) {
                  console.error('Fallback insertion also failed:', fallbackError);
                }
              }
            };
            
            // Add right-click context menu
            blockEl.addEventListener('contextmenu', (e) => {
              e.preventDefault();
              insertBlock();
            });
            
            blocksContainer.appendChild(blockEl);
          }
        });
      }
    });
  });
  
  // Initialize first category (basic)
  // const firstTab = document.querySelector('.tab-btn[data-category="basic"]');
  // if (firstTab) {
  //   firstTab.click();
  // }
  
  // Initialize media category directly
  const mediaTab = document.querySelector('.tab-btn[data-category="media"]');
  if (mediaTab) {
    mediaTab.click();
  } else {
    // If no tab exists, manually initialize media blocks
    const mediaContainer = document.querySelector('#blocks-media');
    if (mediaContainer) {
      const mediaBlocks = blockCategories.media || [];
      mediaBlocks.forEach(blockDef => {
        const blockEl = document.createElement('div');
        blockEl.className = 'gjs-block draggable-block';
        blockEl.innerHTML = \`
          <span>\${blockDef.label}</span>
        \`;
        blockEl.draggable = true;
        
        // Add drag functionality
        blockEl.addEventListener('dragstart', (e) => {
          e.dataTransfer.setData('text/plain', blockDef.id);
          e.dataTransfer.effectAllowed = 'copy';
          
          try {
            editor.BlockManager.drag(blockDef.id);
          } catch (error) {
            console.log('Using fallback drag method');
            const blockModel = blockManager.get(blockDef.id);
            if (blockModel) {
              editor.BlockManager.startDrag(blockModel);
            }
          }
        });
        
        // Add click functionality
        blockEl.addEventListener('click', () => {
          try {
            const selected = editor.getSelected();
            if (selected) {
              selected.append(blockDef.content);
            } else {
              editor.DomComponents.getWrapper().append(blockDef.content);
            }
            console.log('Block added via click:', blockDef.label);
          } catch (error) {
            console.error('Error adding block via click:', error);
          }
        });
        
        mediaContainer.appendChild(blockEl);
      });
    }
  }
}

// Function to setup right panel tabs
function setupRightPanelTabs() {
  const rightTabBtns = document.querySelectorAll('.right-tab-btn');
  const rightPanelSections = document.querySelectorAll('.right-panel-section');
  
  rightTabBtns.forEach(btn => {
    btn.addEventListener('click', (e) => {
      const panel = e.target.dataset.panel;
      
      // Update active tab
      rightTabBtns.forEach(b => b.classList.remove('active'));
      e.target.classList.add('active');
      
      // Update active panel
      rightPanelSections.forEach(p => p.classList.remove('active'));
      const targetPanel = document.getElementById(\`panel-\${panel}\`);
      if (targetPanel) {
        targetPanel.classList.add('active');
      }
      
      // Re-render GrapesJS panels when switching tabs
      if (panel === 'design') {
        setTimeout(() => {
          editor.StyleManager.render();
        }, 100);
      } else if (panel === 'settings') {
        setTimeout(() => {
          editor.TraitManager.render();
        }, 100);
      }
    });
  });
  
  // Initialize first tab (design)
  const firstTab = document.querySelector('.right-tab-btn[data-panel="design"]');
  if (firstTab) {
    firstTab.click();
  }
}

// Enable editing on canvas
editor.on('load', () => {
  console.log('GrapesJS Editor loaded successfully');
  
  // Prevent browser "unsaved changes" popup by removing any beforeunload listeners
  const originalBeforeUnload = window.onbeforeunload;
  window.onbeforeunload = null;
  
  // Also remove any beforeunload event listeners that might be added by GrapesJS
  window.removeEventListener('beforeunload', () => {});
  
  // Initialize Tailwind CSS generation
  try {
    if (editor.runCommand && editor.runCommand('get-tailwindCss')) {
      console.log('Tailwind CSS plugin initialized successfully');
      // Generate initial Tailwind CSS
      editor.runCommand('get-tailwindCss');
    }
  } catch (e) {
    console.log('Tailwind CSS plugin not available:', e);
  }
  
  // Setup categorized blocks and tabs
  setTimeout(() => {
    setupCategorizedBlocks();
    setupRightPanelTabs();
  }, 100);
  
  // Add additional drag and drop event listeners for debugging
  editor.on('block:drag:start', (block) => {
    console.log('Block drag started:', block.get('label'));
  });
  
  editor.on('block:drag:stop', (block) => {
    console.log('Block drag stopped:', block.get('label'));
  });
  
  editor.on('component:add', (component) => {
    console.log('Component added:', component.get('tagName'), component.get('type'));
  });
  
  // Ensure canvas accepts drops
  const canvas = document.querySelector('#gjs-canvas');
  if (canvas) {
    canvas.addEventListener('dragover', (e) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      
      // Check if dragging files
      const files = Array.from(e.dataTransfer.files);
      const hasImageFiles = files.some(file => file.type.startsWith('image/'));
      
      if (hasImageFiles) {
        canvas.classList.add('drag-over');
      }
    });
    
    canvas.addEventListener('dragleave', (e) => {
      // Only remove class if leaving the canvas entirely
      if (!canvas.contains(e.relatedTarget)) {
        canvas.classList.remove('drag-over');
      }
    });
    
    canvas.addEventListener('drop', async (e) => {
      e.preventDefault();
      console.log('Drop event on canvas');
      
      // Remove drag-over styling
      canvas.classList.remove('drag-over');
      
      // Handle file drops
      const files = Array.from(e.dataTransfer.files);
      const imageFiles = files.filter(file => file.type.startsWith('image/'));
      
      if (imageFiles.length > 0) {
        const dropPosition = {
          x: e.clientX,
          y: e.clientY
        };
        
        for (const file of imageFiles) {
          try {
            // Show loading indicator immediately
            const loadingDiv = document.createElement('div');
            loadingDiv.innerHTML = '<div style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); background: rgba(0,0,0,0.8); color: white; padding: 20px; border-radius: 8px; z-index: 10000;"><div style="text-align: center;"><div style="font-size: 24px; margin-bottom: 10px;">⏳</div><div>Uploading image...</div></div></div>';
            document.body.appendChild(loadingDiv);
            
            // Upload image via API immediately
            const imageUrl = await window.onImageUpload(file);
            
            if (imageUrl) {
              // Create image element with the uploaded image
              const imgElement = document.createElement('img');
              imgElement.src = imageUrl;
              imgElement.alt = file.name;
              imgElement.style.maxWidth = '100%';
              imgElement.style.height = 'auto';
              imgElement.style.borderRadius = '8px';
              imgElement.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
              imgElement.style.display = 'block';
              imgElement.style.margin = '20px auto';
              
              // Add to editor at drop position
              const component = editor.DomComponents.addComponent(imgElement.outerHTML);
              const wrapper = editor.DomComponents.getWrapper();
              wrapper.append(component);
              
              // Select the new component
              editor.select(component);
              
              console.log('Image dropped and uploaded successfully:', imageUrl);
            } else {
              // Show error message if upload failed
              const errorDiv = document.createElement('div');
              errorDiv.innerHTML = '<div style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); background: rgba(220,53,69,0.9); color: white; padding: 20px; border-radius: 8px; z-index: 10000;"><div style="text-align: center;"><div style="font-size: 24px; margin-bottom: 10px;">❌</div><div>Upload failed</div></div></div>';
              document.body.appendChild(errorDiv);
              
              // Remove error message after 3 seconds
              setTimeout(() => {
                if (document.body.contains(errorDiv)) {
                  document.body.removeChild(errorDiv);
                }
              }, 3000);
            }
            
            // Remove loading indicator
            if (document.body.contains(loadingDiv)) {
              document.body.removeChild(loadingDiv);
            }
            
          } catch (error) {
            console.error('Error uploading dropped image:', error);
            
            // Show error message
            const errorDiv = document.createElement('div');
            errorDiv.innerHTML = '<div style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); background: rgba(220,53,69,0.9); color: white; padding: 20px; border-radius: 8px; z-index: 10000;"><div style="text-align: center;"><div style="font-size: 24px; margin-bottom: 10px;">❌</div><div>Upload failed</div></div></div>';
            document.body.appendChild(errorDiv);
            
            // Remove error message after 3 seconds
            setTimeout(() => {
              if (document.body.contains(errorDiv)) {
                document.body.removeChild(errorDiv);
              }
            }, 3000);
          }
        }
      }
    });
  }

  // Auto-select first component for editing
  const wrapper = editor.DomComponents.getWrapper();
  if (wrapper) {
    editor.select(wrapper);
  }
  
  // Force canvas to take full dimensions
  setTimeout(() => {
    const canvas = document.querySelector('#gjs-canvas');
    if (canvas) {
      canvas.style.width = '100%';
      canvas.style.height = '100%';
      canvas.style.minWidth = '100%';
      canvas.style.minHeight = '100%';
      canvas.style.margin = '0';
      canvas.style.padding = '0';
      canvas.style.top = '0';
      canvas.style.left = '0';
    }
    
    const gjsFrame = document.querySelector('#gjs iframe');
    if (gjsFrame) {
      gjsFrame.style.width = '100%';
      gjsFrame.style.height = '100%';
      gjsFrame.style.minWidth = '100%';
      gjsFrame.style.minHeight = '100%';
      gjsFrame.style.margin = '0';
      gjsFrame.style.padding = '0';
      gjsFrame.style.top = '0';
      gjsFrame.style.left = '0';
      gjsFrame.style.border = 'none';
    }
    
    // Also fix any GrapesJS internal elements
    const gjsElements = document.querySelectorAll('.gjs-cv-canvas, .gjs-frame, .gjs-cv-canvas iframe');
    gjsElements.forEach(el => {
      el.style.margin = '0';
      el.style.padding = '0';
      el.style.top = '0';
      el.style.left = '0';
      if (el.tagName === 'IFRAME') {
        el.style.border = 'none';
      }
    });
  }, 100);
  
  // Inject project CSS after editor loads - MULTIPLE ATTEMPTS
  setTimeout(() => {
    injectProjectCSS();
    // Second attempt after 1 second
    setTimeout(injectProjectCSS, 1000);
    // Third attempt after 2 seconds
    setTimeout(injectProjectCSS, 2000);
  }, 500);
});

// Re-inject CSS when canvas is updated
editor.on('canvas:update', () => {
  setTimeout(() => {
    injectProjectCSS();
  }, 100);
});

// Inject CSS when canvas frame is ready
editor.on('canvas:frame:load', () => {
  setTimeout(() => {
    injectProjectCSS();
    // Additional injection after frame load
    setTimeout(injectProjectCSS, 500);
  }, 100);
});

// Inject CSS when components are rendered
editor.on('component:mount', () => {
  setTimeout(injectProjectCSS, 50);
});

// Inject CSS when editor is ready
editor.on('canvas:render', () => {
  setTimeout(injectProjectCSS, 100);
});

// Enable double-click to edit text
editor.on('component:selected', (component) => {
  if (component.get('type') === 'text') {
    component.set('editable', true);
  }
});

// Handle image upload placeholders
editor.on('component:selected', (component) => {
  const el = component.getEl();
  if (el && el.classList.contains('image-upload-placeholder')) {
    // Create file input for image upload
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*';
    fileInput.style.display = 'none';
    document.body.appendChild(fileInput);
    
    fileInput.addEventListener('change', async (e) => {
      const file = e.target.files[0];
      if (file) {
        try {
          // Show loading state
          el.innerHTML = '<div style="text-align: center; padding: 20px;"><div style="font-size: 24px; color: #007bff; margin-bottom: 10px;">⏳</div><div style="color: #6c757d; font-size: 14px;">Uploading...</div></div>';
          
          // Call the upload function (will be set by parent)
          const imageUrl = await window.onImageUpload(file);
          
          if (imageUrl) {
            // Create image HTML content
            const imageHtml = \`<img src="\${imageUrl}" alt="\${file.name}" style="max-width: 100%; height: auto; border-radius: 8px; box-shadow: 0 4px 8px rgba(0,0,0,0.1); display: block; margin: 20px auto;">\`;
            
            // Create a new component with the image
            const newComponent = editor.DomComponents.addComponent(imageHtml);
            
            // Replace the current component with the new image component
            component.replaceWith(newComponent);
            
            // Select the new component
            editor.select(newComponent);
            
            console.log('Image uploaded and inserted successfully:', imageUrl);
          } else {
            // Show error state
            el.innerHTML = '<div style="text-align: center; padding: 20px;"><div style="font-size: 24px; color: #dc3545; margin-bottom: 10px;">❌</div><div style="color: #6c757d; font-size: 14px;">Upload failed</div></div>';
          }
        } catch (error) {
          console.error('Image upload error:', error);
          el.innerHTML = '<div style="text-align: center; padding: 20px;"><div style="font-size: 24px; color: #dc3545; margin-bottom: 10px;">❌</div><div style="color: #6c757d; font-size: 14px;">Upload failed</div></div>';
        }
      }
      
      // Clean up file input
      document.body.removeChild(fileInput);
    });
    
    // Trigger file input click
    fileInput.click();
  }
  
  // Handle project assets selection
  if (el && el.classList.contains('image-assets-placeholder')) {
    try {
      // Show loading state
      el.innerHTML = '<div style="text-align: center; padding: 20px;"><div style="font-size: 24px; color: #28a745; margin-bottom: 10px;">⏳</div><div style="color: #6c757d; font-size: 14px;">Opening asset selector...</div></div>';
      
      // Request asset selection from parent
      const messageId = Date.now().toString();
      
      const handleAssetResponse = (event) => {
        if (event.data && event.data.type === 'ASSET_SELECTION_RESPONSE' && event.data.messageId === messageId) {
          window.removeEventListener('message', handleAssetResponse);
          
          if (event.data.success && event.data.selectedAsset) {
            const asset = event.data.selectedAsset;
            
            // Create image HTML content
            const imageHtml = \`<img src="\${asset.url}" alt="\${asset.name}" style="max-width: 100%; height: auto; border-radius: 8px; box-shadow: 0 4px 8px rgba(0,0,0,0.1); display: block; margin: 20px auto;">\`;
            
            // Create a new component with the image
            const newComponent = editor.DomComponents.addComponent(imageHtml);
            
            // Replace the current component with the new image component
            component.replaceWith(newComponent);
            
            // Select the new component
            editor.select(newComponent);
            
            console.log('Asset selected and inserted successfully:', asset.url);
          } else {
            // Show error or cancelled state
            el.innerHTML = '<div style="text-align: center; padding: 20px;"><div style="font-size: 24px; color: #6c757d; margin-bottom: 10px;">📁</div><div style="color: #6c757d; font-size: 14px;">No asset selected</div></div>';
          }
        }
      };
      
      window.addEventListener('message', handleAssetResponse);
      
      // Send asset selection request to parent
      window.parent.postMessage({
        type: 'ASSET_SELECTION_REQUEST',
        messageId: messageId
      }, '*');
      
      // Timeout after 30 seconds
      setTimeout(() => {
        window.removeEventListener('message', handleAssetResponse);
        el.innerHTML = '<div style="text-align: center; padding: 20px;"><div style="font-size: 24px; color: #dc3545; margin-bottom: 10px;">❌</div><div style="color: #6c757d; font-size: 14px;">Selection timeout</div></div>';
      }, 30000);
      
    } catch (error) {
      console.error('Asset selection error:', error);
      el.innerHTML = '<div style="text-align: center; padding: 20px;"><div style="font-size: 24px; color: #dc3545; margin-bottom: 10px;">❌</div><div style="color: #6c757d; font-size: 14px;">Selection failed</div></div>';
    }
  }
});

// Comprehensive event listeners for all types of edits
const changeEvents = [
  // Component events
  'component:add',
  'component:remove', 
  'component:update',
  'component:create',
  'component:mount',
  'component:unmount',
  'component:clone',
  'component:drag:end',
  'component:styleUpdate',
  'component:sync',
  
  // Style events
  'style:change',
  'style:property:update',
  'style:sector:update',
  'style:target',
  
  // Block events
  'block:drag:stop',
  
  // Canvas events  
  'canvas:dragstop',
  'canvas:drop',
  
  // Storage events
  'storage:load',
  'storage:store',
  
  // Layer events
  'layer:component:update',
  
  // Trait events
  'trait:change',
  
  // Selector events
  'selector:add',
  'selector:remove',
  'selector:update',
  
  // Rule events
  'rule:add',
  'rule:remove',
  'rule:update',
  
  // Device events
  'device:select',
  
  // Undo/Redo events
  'undo',
  'redo'
];

// Listen to all change events
changeEvents.forEach(event => {
  editor.on(event, debouncedOnChange);
});

// Additional specific handlers for more granular control
editor.on('component:drag:end', () => {
  setTimeout(debouncedOnChange, 100); // Small delay for drag operations
});

editor.on('run:preview stop:preview', () => {
  // Don't trigger changes for preview mode toggles
});

// Handle text editing specifically
editor.on('rte:enable', () => {
  // Rich text editor enabled
  console.log('Text editing started');
});

editor.on('rte:disable', () => {
  // Rich text editor disabled - content likely changed
  debouncedOnChange();
});

// Keyboard shortcuts
document.addEventListener('keydown', (e) => {
  // Ctrl+S or Cmd+S to save
  if ((e.ctrlKey || e.metaKey) && e.key === 's') {
    e.preventDefault();
    if (hasUnsavedChanges) {
      handleSave();
    }
  }
});
</script>`;
}; 




